import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { EntitlementCard, PAY_PER_MEETING } from '@copilot/ui';
import { ANALYTICS_EVENTS, track } from '../../services/analytics';
import * as billingApi from '../../services/billingApi';

// Where each entitlement action goes on Web. The action itself - which
// one, and its label - is the backend's decision (GET /api/billing/
// entitlement); this only maps it to a route.
const ACTION_ROUTES = {
  start_meeting: '/app/meeting/new',
  buy_meeting_pass: PAY_PER_MEETING.buyPath,
  buy_extra_meeting: PAY_PER_MEETING.buyPath,
  // The add-on panel lives on Plan & Billing.
  buy_plan_addon: '/app/billing',
  upgrade: '/app/billing/plans',
  view_plans: '/app/billing/plans',
  manage_subscription: '/app/billing',
  update_payment: '/app/billing',
  contact_support: '/contact',
};

// The dashboard's plan card: current plan, meetings left, renewal or
// expiry, and the next best action - so nobody has to open Plan & Billing
// to find out where they stand. Renders nothing when pricing is not
// enforced on this deployment.
function DashboardEntitlement() {
  const navigate = useNavigate();
  const [entitlement, setEntitlement] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      setEntitlement(await billingApi.getEntitlement());
    } catch (err) {
      setError(err?.message || "We couldn't load your plan.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onAction = useCallback(
    (action) => {
      const context = { status: entitlement?.status, plan_code: entitlement?.plan?.code };
      if (
        action.code === 'buy_extra_meeting' ||
        action.code === 'buy_meeting_pass' ||
        action.code === 'buy_plan_addon'
      ) {
        track(ANALYTICS_EVENTS.EXTRA_MEETING_CLICKED, context);
      } else if (action.code === 'upgrade') {
        track(ANALYTICS_EVENTS.UPGRADE_CLICKED, {
          ...context,
          target_plan_code: entitlement?.upgrade_plan?.code,
        });
      }
      navigate(ACTION_ROUTES[action.code] || '/app/billing');
    },
    [entitlement, navigate]
  );

  const onLowBalanceViewed = useCallback((viewed) => {
    track(ANALYTICS_EVENTS.CREDIT_LOW_BANNER_VIEWED, {
      status: viewed.status,
      meetings_remaining: viewed.meetings_remaining,
      plan_code: viewed.plan?.code,
    });
  }, []);

  return (
    <div className="dashboard-entitlement">
      <EntitlementCard
        entitlement={entitlement}
        loading={isLoading}
        error={error}
        onRetry={load}
        onAction={onAction}
        onLowBalanceViewed={onLowBalanceViewed}
      />
    </div>
  );
}

export default DashboardEntitlement;
